import { useMemo } from 'react';
import { useHydratedDealersByCity } from './useHydratedDealersByCity';
import { useDealersInfiniteScroll } from './useDealersInfiniteScroll';
import { isSSR } from '@/lib/isSSR';

interface UseDealersByCityWithInfiniteScrollOptions {
  city: string;
  enableInfiniteScroll?: boolean;
  pageSize?: number;
}

/**
 * Hook che estende useHydratedDealersByCity con funzionalità di infinite scroll
 * Mantiene piena compatibilità con il comportamento esistente
 * Se enableInfiniteScroll è false o si verificano errori, fallback al comportamento originale
 */
export function useDealersByCityWithInfiniteScroll(
  options: UseDealersByCityWithInfiniteScrollOptions
) {
  const { city, enableInfiniteScroll = false, pageSize = 20 } = options;
  const isClient = !isSSR();
  
  // Determina se usare infinite scroll PRIMA di chiamare gli hook
  const shouldUseInfiniteScroll = enableInfiniteScroll && isClient;
  
  // Hook originale come fallback - non accetta parametri
  const originalData = useHydratedDealersByCity();
  
  // Configurazione per infinite scroll
  const infiniteScrollOptions = useMemo(() => ({
    city,
    pageSize,
    enableInfiniteScroll: shouldUseInfiniteScroll,
    initialFilters: {
      sortOrder: "distance" as const,
      filterActivated: false
    }
  }), [city, pageSize, shouldUseInfiniteScroll]);
  
  // Hook infinite scroll (SOLO se abilitato)
  const infiniteScrollData = useDealersInfiniteScroll(
    shouldUseInfiniteScroll ? infiniteScrollOptions : { enableInfiniteScroll: false }
  );
  
  // Combina i dati mantenendo la struttura originale
  const result = useMemo(() => {
    if (shouldUseInfiniteScroll) {
      // Usa infinite scroll con dati aggiuntivi
      return {
        ...originalData,
        cityDealers: infiniteScrollData.dealers, // Usa cityDealers invece di dealers
        isLoading: infiniteScrollData.isLoading,
        
        // Proprietà aggiuntive per infinite scroll
        isLoadingMore: infiniteScrollData.isLoadingMore,
        hasMore: infiniteScrollData.hasMore,
        totalCount: infiniteScrollData.totalCount,
        enableInfiniteScroll: true,
        loadMore: infiniteScrollData.loadMore,
        
        // Mantieni filtri e azioni
        filters: infiniteScrollData.filters,
        updateFilters: infiniteScrollData.updateFilters,
        handleRefresh: infiniteScrollData.refresh
      };
    } else {
      // Fallback al comportamento originale
      return {
        ...originalData,
        
        // Proprietà infinite scroll disabilitate
        isLoadingMore: false,
        hasMore: false,
        totalCount: originalData.cityDealers?.length || 0,
        enableInfiniteScroll: false,
        loadMore: () => {},
        
        // Mantieni comportamento originale per filtri
        filters: {
          search: "",
          range: [0, 5] as [number, number],
          filterVerified: false,
          filterActivated: false,
          sortOrder: "distance" as const,
          distanceRange: 0,
        },
        updateFilters: () => {},
        handleRefresh: originalData.handleRefresh || (() => {})
      };
    }
  }, [
    shouldUseInfiniteScroll,
    originalData,
    infiniteScrollData.dealers,
    infiniteScrollData.isLoading,
    infiniteScrollData.isLoadingMore,
    infiniteScrollData.hasMore,
    infiniteScrollData.totalCount,
    infiniteScrollData.loadMore,
    infiniteScrollData.filters,
    infiniteScrollData.updateFilters,
    infiniteScrollData.refresh
  ]);
  
  return result;
}
